import React, { useCallback, useMemo } from "react";
import {
  Carousel,
  CarouselContent,
  CarouselDots,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from "ui/Carousel";
import VideoPlayer from "components/Common/VideoPlayer";
import type { ExistingMediaFile } from "util/mediaTypes";
import CarouselImage from "ui/CarouselImage";
import { resolveMediaUrl } from "util/mediaUrl";
import { isSaneAspectRatio } from "util/mediaAspect";
import {
  type MediaAspectHint,
  useMediaAspect,
} from "hooks/useMediaAspect";

import { cn } from "util/cn";

export interface MediaCarouselProps {
  mediaFiles: ExistingMediaFile[];
  className?: string;
  imageCdn?: string;
  showDots?: boolean;
  showArrows?: boolean;
  /**
   * Called when the user taps anywhere on the media surface (image area or
   * inactive video frame). The handler receives the index of the active
   * item. Controls (arrows/dots/play) stop propagation, so they do not fire
   * this callback. Provide this only on consumers that should open the
   * full-screen reels surface; otherwise leave it undefined and clicks will
   * bubble naturally to a parent card.
   */
  onMediaTap?: (activeIndex: number) => void;
  /**
   * When true, allows the carousel to expand to taller aspect ratios (e.g. 9:16)
   * instead of clamping to the standard 3:4 card ratio. Used by MediaViewer.
   */
  allowFullHeight?: boolean;
}

const defaultImageCdn = process.env.REACT_APP_IMAGE_CDN || "";

const buildItemId = (file: ExistingMediaFile, index: number): string => {
  return `${file.key || "no-key"}_${file.order || index}`;
};

const buildHints = (
  files: ReadonlyArray<ExistingMediaFile>,
): MediaAspectHint[] =>
  files.map((file, index) => ({
    id: buildItemId(file, index),
    aspectRatio: file.extended_metadata?.aspectRatio ?? null,
  }));

/**
 * Card-level horizontal media carousel.
 *
 * Sizing strategy:
 * - Pulls any server-stored `extended_metadata.aspectRatio` for an initial
 *   group aspect, then refines as items decode and report `naturalWidth /
 *   naturalHeight` via the shared `useMediaAspect` hook.
 * - The resolved aspect is clamped between 3:4 (portrait edge) and 16:9
 *   (landscape edge) so cards never explode to full viewport height for
 *   tall portrait content; out-of-band media is letterboxed inside the
 *   bounded frame via `object-contain`.
 *
 * Failure handling:
 * - `CarouselImage` retries once with a fresh signed URL on error, then
 *   falls back to `<object type="image/svg+xml">` for SVGs, then to an
 *   inline placeholder.
 */
const MediaCarousel: React.FC<MediaCarouselProps> = ({
  mediaFiles,
  className,
  imageCdn = defaultImageCdn,
  showDots = true,
  showArrows = true,
  onMediaTap,
  allowFullHeight = false,
}) => {
  const sorted = useMemo<ExistingMediaFile[]>(() => {
    return [...mediaFiles].sort((a, b) => (a.order || 0) - (b.order || 0));
  }, [mediaFiles]);

  const hints = useMemo<MediaAspectHint[]>(() => buildHints(sorted), [sorted]);
  
  // Use looser bounds if allowFullHeight is true, otherwise use standard card bounds
  const { aspectStyle, onMeasure } = useMediaAspect(hints, {
    min: allowFullHeight ? 9/16 : undefined, // Allows standard 9:16 portrait video
    max: allowFullHeight ? 16/9 : undefined,
  });

  const [activeIndex, setActiveIndex] = React.useState<number>(0);

  const handleSurfaceClick = useCallback(
    (index: number) => (event: React.MouseEvent<HTMLElement>): void => {
      if (!onMediaTap) {
        return;
      }
      // Only react to clicks on the surface itself; controls stop
      // propagation explicitly so their clicks never reach this handler.
      onMediaTap(index);
      event.stopPropagation();
    },
    [onMediaTap],
  );

  if (sorted.length === 0) return null;

  const renderItem = (file: ExistingMediaFile, idx: number): React.ReactNode => {
    const itemId = buildItemId(file, idx);
    const url = resolveMediaUrl(
      {
        key: file.key,
        url: (file as { url?: string }).url,
        signed_url: (file as { signed_url?: string }).signed_url,
      },
      imageCdn,
    );
    const isImage = file.type === "image";

    return (
      <div
        key={itemId}
        className="relative w-full h-full max-h-full flex items-center justify-center overflow-hidden bg-transparent"
        style={allowFullHeight ? undefined : aspectStyle}
        // Clicks on the wrapper invoke onMediaTap; controls inside stop
        // propagation. `insideCard` consumers should also stopPropagation
        // here when onMediaTap is provided so the outer card click is not
        // double-fired.
        onClick={onMediaTap ? handleSurfaceClick(idx) : undefined}
        role={onMediaTap ? "button" : undefined}
        tabIndex={onMediaTap ? 0 : undefined}
        onKeyDown={
          onMediaTap
            ? (event) => {
                if (event.key === "Enter" || event.key === " ") {
                  event.preventDefault();
                  onMediaTap(idx);
                }
              }
            : undefined
        }
        aria-label={onMediaTap ? "Open media in full screen" : undefined}
      >
        {isImage ? (
          <CarouselImage
            source={{
              id: itemId,
              key: file.key,
              url: (file as { url?: string }).url,
              signed_url: (file as { signed_url?: string }).signed_url,
              mimetype: file.mimetype,
              imageCdn,
              alt: file.originalName || `Media ${idx + 1}`,
            }}
            onMeasure={onMeasure}
            className="w-full h-full max-h-full object-contain object-center"
            onClick={onMediaTap ? handleSurfaceClick(idx) : undefined}
          />
        ) : (
          <VideoPlayer
            url={url}
            muted
            controls
            objectFit="contain"
            className="w-full h-full max-h-full"
            // Only the explicit fullscreen button enters native fullscreen.
            // In a card carousel, click forwards to `onMediaTap` (open
            // MediaViewer); inside MediaViewer (no `onMediaTap`), click
            // toggles play/pause.
            disableClickFullscreen
            isActive={idx === activeIndex}
            shareStateAcrossInstances
            onClick={
              onMediaTap
                ? (e) => {
                    onMediaTap(idx);
                    e.stopPropagation();
                  }
                : undefined
            }
          />
        )}
        {sorted.length > 1 && (
          <div className="absolute top-2 right-2 bg-white bg-opacity-80 text-black rounded-full px-2 py-1 text-xs pointer-events-none">
            {idx + 1}/{sorted.length}
          </div>
        )}
      </div>
    );
  };

  if (sorted.length === 1) {
    // Single-item path: still wrap in the bounded frame so the card height
    // matches the resolved aspect once the image decodes.
    return <div className={cn("w-full h-full max-h-full flex items-center justify-center", className)}>{renderItem(sorted[0], 0)}</div>;
  }

  return (
    <div className={cn("w-full h-full max-h-full", className)}>
      <Carousel
        className="w-full h-full max-h-full"
        setApi={(api) => {
          if (!api) return;
          setActiveIndex(api.selectedScrollSnap());
          api.on("select", () => setActiveIndex(api.selectedScrollSnap()));
        }}
      >
        <CarouselContent className="h-full max-h-full">
          {sorted.map((file, idx) => (
            <CarouselItem
              key={buildItemId(file, idx)}
              className="relative h-full max-h-full flex items-center justify-center"
            >
              {renderItem(file, idx)}
            </CarouselItem>
          ))}
        </CarouselContent>
        {showArrows && (
          <>
            <CarouselPrevious
              transparent
              className="absolute left-2 top-1/2 -translate-y-1/2"
            />
            <CarouselNext
              transparent
              className="absolute right-2 top-1/2 -translate-y-1/2"
            />
          </>
        )}
        {showDots && <CarouselDots />}
      </Carousel>
    </div>
  );
};

// Re-export of the intrinsic guard so carousel-focused tests can import a
// single symbol from this module without depending on the util file directly.
export const __test__ = { isSaneAspectRatio };

export default MediaCarousel;
